// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import './App.css';
import WallpaperScene from './WallpaperScene';

function Page({ title }) {

  /* =========================
     🔹 ABOUT PAGE
     ========================= */
  if (title === 'About') {
    return (
      <div style={aboutPageWrapperStyle}>

        {/* 🔳 Blurred Background */}
        <div style={aboutBackgroundStyle} />

        {/* 🔲 Foreground Content */}
        <div style={aboutContentStyle}>
          <h1 style={aboutTitleStyle}>About the Creator</h1>

          <p style={aboutParagraphStyle}>
            I go by the name Barcode, and I’m a gamer at heart. I grew up playing a wide variety of games:
            turn-based RPGs, sports titles, FPS games, strategy — you name it. As I got older, I began to
            notice an unfortunate trend: game quality was declining. Titles felt rushed, buggy, and
            uninspired — released year after year with little to no growth.
            <br /><br />
            That realization led me into the world of modding. What began as a way to improve games quickly
            became a hands-on education in reverse engineering through memory inspection and debugging,
            scripting, hex editing, asset pipelines, and engine-level systems. Along the way, I developed
            skills in 3D modeling, rigging, animation, and low-level tooling — creating standalone
            executables using non-traditional compilers for greater control and security.
          </p>

          <p style={aboutParagraphStyle}>
            Over time, I realized the skills I was building were game development at its core. That led me
            to Unreal Engine, where I now work primarily with C++ and Blueprints, developing systems for UI,
            AI, level design, and multiplayer replication.
            <br /><br />
            My latest project — <strong>Project 404</strong> — is the result of that journey. Inspired by
            <em> Left 4 Dead</em> and <em> Resident Evil</em>, it’s an up to 4 player horror experience
            where survival is only the beginning.
          </p>

          {/* 🔗 Social Buttons */}
          <div style={socialButtonRowStyle}>
            <a href="https://www.patreon.com/barcode_exe" style={socialButtonStyle} target="_blank" rel="noreferrer">Patreon</a>
            <a href="https://discord.gg/kKvdeQZTTM" style={socialButtonStyle} target="_blank" rel="noreferrer">Discord</a>
            <a href="https://www.youtube.com/@Barcode6321" style={socialButtonStyle} target="_blank" rel="noreferrer">YouTube</a>
          </div>
        </div>
      </div>
    );
  }

  /* =========================
     🔹 DEFAULT PAGE TEMPLATE
     ========================= */
  return (
    <div style={{
      color: 'white',
      padding: '4rem',
      fontSize: '2rem',
      textAlign: 'center',
      minHeight: '100vh',
      background: 'black',
      paddingTop: '120px'
    }}>
      {title} Content Goes Here.
    </div>
  );
}

function App() {
  return (
    <Router>

      {/* 🔳 TOP BANNER */}
      <div style={bannerStyle}>

        {/* 🟥 BRAND */}
        <div style={brandContainerStyle}>
          <img
            src={process.env.PUBLIC_URL + '/barcodelogo.png'}
            alt="Barcode Logo"
            style={brandLogoStyle}
          />
          <span style={brandTextStyle}>BARCODE EXECUTIVE L.L.C.</span>
        </div>

        {/* 🟦 NAV */}
        <div style={navContainerStyle}>
          <Link to="/" style={linkStyle}>Home</Link>

          <a
            href="https://www.patreon.com/posts/test-post-147132806"
            style={linkStyle}
            target="_blank"
            rel="noreferrer"
          >
            Download
          </a>

          <a
            href="https://www.youtube.com/playlist?list=PL975qyDTEXmlXeUmUd1onz4n6ez3Rq9GF"
            style={linkStyle}
            target="_blank"
            rel="noreferrer"
          >
            Devlogs
          </a>

          <Link to="/page3" style={linkStyle}>About</Link>
        </div>
      </div>

      {/* 🔥 ROUTES */}
      <Routes>
        <Route
          path="/"
          element={
            <div style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}>
              <WallpaperScene />
            </div>
          }
        />
        <Route path="/page1" element={<Page title="Download" />} />
        <Route path="/page2" element={<Page title="Devlogs" />} />
        <Route path="/page3" element={<Page title="About" />} />
      </Routes>

    </Router>
  );
}

/* =========================
   🎯 STYLES
   ========================= */

const bannerStyle = {
  position: 'fixed',
  top: 0,
  width: '100%',
  height: '60px',
  backgroundColor: 'rgba(0,0,0,0.7)',
  backdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'center',
  zIndex: 10
};

const brandContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  position: 'relative',
  left: '2%'
};

const brandLogoStyle = {
  width: '51px',
  height: '51px',
  objectFit: 'contain'
};

const brandTextStyle = {
  color: 'white',
  fontSize: '1.8rem',
  fontWeight: 'bold',
  whiteSpace: 'nowrap'
};

const navContainerStyle = {
  display: 'flex',
  gap: '2rem',
  position: 'relative',
  left: '55%'
};

const linkStyle = {
  color: 'white',
  fontSize: '1.2rem',
  textDecoration: 'none',
  fontWeight: 'bold',
  whiteSpace: 'nowrap'
};

/* =========================
   ABOUT PAGE BACKGROUND
   ========================= */

const aboutPageWrapperStyle = {
  position: 'relative',
  minHeight: '100vh',
  overflow: 'hidden',
  color: 'white'
};

const aboutBackgroundStyle = {
  position: 'absolute',
  inset: 0,
  backgroundImage: `url(${process.env.PUBLIC_URL + '/wallpaper.jpg'})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  filter: 'blur(10px) brightness(0.35)', // 🎚️ adjust blur strength here
  transform: 'scale(1.05)',
  zIndex: 0
};

const aboutContentStyle = {
  position: 'relative',
  zIndex: 1,
  paddingTop: '120px',
  paddingLeft: '10%',
  paddingRight: '10%'
};

const aboutTitleStyle = {
  fontSize: '2.8rem',
  marginBottom: '1.5rem'
};

const aboutParagraphStyle = {
  fontSize: '1.2rem',
  lineHeight: '1.7',
  marginBottom: '1.5rem',
  maxWidth: '900px'
};

const socialButtonRowStyle = {
  display: 'flex',
  gap: '1.5rem',
  marginTop: '3rem'
};

const socialButtonStyle = {
  padding: '0.8rem 1.6rem',
  backgroundColor: '#333',
  color: 'white',
  textDecoration: 'none',
  fontSize: '1rem',
  fontWeight: 'bold',
  borderRadius: '6px'
};

export default App;
